<script setup>
import { onUnmounted, watch, ref } from 'vue';
import { useStore } from '../store.js';
import api from '../ApiClient.js';

const { store, clearJob } = useStore();
const pollIntervals = new Map();

const visible = ref(false);
let hideTimeout = null;

const fetchJob = async (jobId) => {
  try {
    const job = await api.getJobStatus(jobId);
    store.jobs.set(jobId, job);
    if (job.status === 'done' || job.status === 'error') {
      if (pollIntervals.has(jobId)) {
        clearInterval(pollIntervals.get(jobId));
        pollIntervals.delete(jobId);
      }
      if (job.status === 'done' && job.result && job.result.id) {
        try {
          const movie = await api.getMovie(job.result.id);
          store.currentMovie = movie;
          const index = store.movies.findIndex((m) => m.id === movie.id);
          if (index !== -1) {
            store.movies[index] = movie;
          } else {
            store.movies.push(movie);
          }
        } catch (error) {
          console.error('Failed to fetch updated movie:', error);
        }
      }
    }
  } catch (error) {
    console.error(`Failed to fetch job status for ${jobId}:`, error);
    store.jobs.delete(jobId);
    if (pollIntervals.has(jobId)) {
      clearInterval(pollIntervals.get(jobId));
      pollIntervals.delete(jobId);
    }
  }
};

const startPolling = (jobId) => {
  if (pollIntervals.has(jobId)) return;
  fetchJob(jobId);
  const interval = setInterval(() => fetchJob(jobId), 2000);
  pollIntervals.set(jobId, interval);
};

const stopPolling = (jobId) => {
  if (pollIntervals.has(jobId)) {
    clearInterval(pollIntervals.get(jobId));
    pollIntervals.delete(jobId);
  }
};

function hasActiveJobs() {
  return Array.from(store.jobs.values()).some((job) => job.status === 'pending' || job.status === 'running');
}

function allJobsCompleted() {
  return store.jobs.size > 0 && Array.from(store.jobs.values()).every((job) => job.status === 'done' || job.status === 'error');
}

watch(
  () => Array.from(store.jobs.keys()),
  (newJobIds, oldJobIds) => {
    // If a new job appears, show the handler
    if (newJobIds.length > 0 && oldJobIds && newJobIds.length > oldJobIds.length) {
      visible.value = true;
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
    }
    newJobIds.forEach((jobId) => {
      const job = store.jobs.get(jobId);
      if (job && (job.status === 'pending' || job.status === 'running')) {
        startPolling(jobId);
      }
    });
    if (oldJobIds) {
      oldJobIds.forEach((jobId) => {
        if (!newJobIds.includes(jobId)) {
          stopPolling(jobId);
        }
      });
    }
  },
  { deep: true }
);

watch(
  () => store.jobs.size,
  (size) => {
    if (size === 0) {
      visible.value = false;
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
    }
  }
);

watch(
  () => Array.from(store.jobs.values()).map((j) => j.status),
  (statuses) => {
    if (allJobsCompleted()) {
      if (hideTimeout) clearTimeout(hideTimeout);
      hideTimeout = setTimeout(() => {
        visible.value = false;
      }, 2500);
    } else if (hasActiveJobs()) {
      visible.value = true;
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
    }
  }
);

onUnmounted(() => {
  pollIntervals.forEach((interval) => clearInterval(interval));
  pollIntervals.clear();
  if (hideTimeout) clearTimeout(hideTimeout);
});

function getJobDescription(job) {
  if (!job) return '';
  if (job.service === 'MovieService' && job.method === 'enrichMovie') {
    return 'Generating movie details from prompt...';
  }
  if (job.service === 'MovieService' && job.method === 'createMovie') {
    return 'Creating movie...';
  }
  if (job.service === 'MovieService' && job.method === 'updateMovie') {
    return 'Updating movie...';
  }
  if (job.service === 'SystemService') {
    return `System: ${job.method}`;
  }
  return `${job.service || 'UnknownService'}: ${job.method || 'unknownMethod'}`;
}
</script>
<template>
  <transition name="job-handler-fade-slide">
    <div v-if="visible && store.jobs.size > 0" class="job-handler">
      <button class="job-handler-close" v-if="hasActiveJobs()" @click="clearJob(Array.from(store.jobs.keys())[0])" title="Cancel Job">&times;</button>
      <div v-for="[jobId, job] in store.jobs" :key="jobId" class="job-item">
        <div class="job-header">
          <span class="job-id">Job: {{ jobId.substring(0, 8) }}...</span>
          <span class="job-status" :class="job.status">{{ job.status }}</span>
        </div>
        <div class="job-desc">{{ getJobDescription(job) }}</div>
        <div v-if="job.status === 'pending' || job.status === 'running'" class="job-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: (job.progress || 0) + '%' }"></div>
          </div>
          <span class="progress-text">{{ job.progress || 0 }}%</span>
        </div>
        <div v-if="job.status === 'error'" class="job-error">Error: {{ job.error }}</div>
        <div v-if="job.status === 'done'" class="job-success">Completed successfully</div>
        <button v-if="job.status === 'pending'" @click="clearJob(jobId)" class="cancel-job-btn">Cancel</button>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.job-handler {
  max-width: 480px;
  margin: 24px auto 0 auto;
  padding: 0 8px;
  position: absolute;
  right: 0;
  top: 50px;
  z-index: 1002;
}
/* Animation for job handler */
.job-handler-fade-slide-enter-active,
.job-handler-fade-slide-leave-active {
  transition:
    opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.job-handler-fade-slide-enter-from,
.job-handler-fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-30px) translateX(40px) scale(0.98);
}
.job-handler-fade-slide-enter-to,
.job-handler-fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0) translateX(0) scale(1);
}
.job-handler-close {
  position: absolute;
  top: 8px;
  right: 12px;
  background: none;
  border: none;
  font-size: 1.7em;
  color: #888;
  cursor: pointer;
  z-index: 1003;
  transition: color 0.2s;
}
.job-handler-close:hover {
  color: #d32f2f;
}
.job-item {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  margin-bottom: 18px;
  padding: 18px 20px 14px 20px;
  position: relative;
  border-left: 4px solid var(--color-primary);
}
.job-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.job-id {
  font-size: 0.98em;
  color: #888;
}
.job-status {
  font-size: 1em;
  font-weight: 600;
  text-transform: capitalize;
  padding: 2px 10px;
  border-radius: 12px;
  background: #f1f1f1;
  color: #555;
}
.job-status.pending,
.job-status.running {
  background: #fffbe6;
  color: #bfa100;
}
.job-status.done {
  background: #e6ffed;
  color: #1a7f37;
}
.job-status.error {
  background: #ffeaea;
  color: #d32f2f;
}
.job-progress {
  margin: 10px 0 6px 0;
}
.progress-bar {
  width: 100%;
  height: 10px;
  background: #f0f0f0;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 4px;
}
.progress-fill {
  height: 100%;
  background: var(--color-primary);
  transition: width 0.3s;
}
.progress-text {
  font-size: 0.95em;
  color: #666;
}
.job-error {
  color: #d32f2f;
  background: #ffeaea;
  padding: 8px;
  border-radius: 4px;
  margin-bottom: 6px;
}
.job-success {
  color: #1a7f37;
  background: #e6ffed;
  padding: 8px;
  border-radius: 4px;
  margin-bottom: 6px;
}
.cancel-job-btn {
  background: #fffbe6;
  color: #bfa100;
  border: none;
  border-radius: 4px;
  padding: 6px 14px;
  font-size: 0.98em;
  cursor: pointer;
  margin-top: 6px;
  transition: background 0.2s;
}
.cancel-job-btn:hover {
  background: #fff3c1;
}
.job-desc {
  font-size: 1.05em;
  color: #444;
  margin-bottom: 8px;
  font-style: italic;
}
</style>
